'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  Eye,
  FileText,
  FolderOpen,
  Send,
  Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { SharedFileListItem } from '@/lib/smart-share/list-shared-files';

/**
 * Smart Share (Free) dashboard body — a share-centric Home shown instead of the
 * rooms KPIs/chart/room-list. Fed by durable Quick Share data (`/api/shared-files`).
 */
export function SmartShareDashboardBody() {
  const [files, setFiles] = useState<SharedFileListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/shared-files?limit=50', {
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('Failed to load shared files');
        const data = await res.json();
        if (!cancelled) setFiles(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!cancelled) setFiles([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalFiles = files.length;
  const totalShares = files.reduce((sum, f) => sum + f.shareCount, 0);
  const totalViews = files.reduce((sum, f) => sum + f.firstViewCount, 0);
  const recentFiles = files.slice(0, 5);

  const kpis = [
    {
      label: 'Files shared',
      value: totalFiles,
      icon: FileText,
      accent: 'emerald',
    },
    {
      label: 'Total shares sent',
      value: totalShares,
      icon: Share2,
      accent: 'blue',
    },
    {
      label: 'Recipients who opened',
      value: totalViews,
      icon: Eye,
      accent: 'violet',
    },
  ] as const;

  const accentMap: Record<string, { bar: string; iconBg: string; icon: string }> = {
    emerald: {
      bar: 'bg-emerald-400/80',
      iconBg: 'bg-emerald-100/80',
      icon: 'text-emerald-600',
    },
    blue: {
      bar: 'bg-blue-400/80',
      iconBg: 'bg-blue-100/80',
      icon: 'text-blue-600',
    },
    violet: {
      bar: 'bg-violet-400/80',
      iconBg: 'bg-violet-100/80',
      icon: 'text-violet-600',
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {kpis.map((kpi) => {
          const accent = accentMap[kpi.accent];
          const Icon = kpi.icon;
          return (
            <Card
              key={kpi.label}
              className="relative overflow-hidden rounded-2xl border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className={`absolute inset-y-0 left-0 w-1 ${accent.bar}`} />
              <CardContent className="p-4">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <p className="text-sm font-medium tracking-wide text-slate-500">
                    {kpi.label}
                  </p>
                  <div className={`shrink-0 rounded-xl p-2 ${accent.iconBg}`}>
                    <Icon className={`h-4 w-4 ${accent.icon}`} />
                  </div>
                </div>
                {isLoading ? (
                  <Skeleton className="mb-1 h-8 w-14" />
                ) : (
                  <div className="text-3xl font-semibold tabular-nums tracking-tight text-slate-900">
                    {kpi.value.toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-slate-200/80 shadow-sm lg:col-span-2">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">
                Recent shared files
              </p>
              <Button asChild variant="ghost" size="sm" className="h-8">
                <Link href="/shared-files">View all</Link>
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl border border-slate-100 p-3"
                  >
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <div className="mb-4 rounded-full bg-blue-50 p-4">
                  <Share2 className="h-8 w-8 text-blue-500" />
                </div>
                <h3 className="mb-1 text-lg font-semibold text-slate-900">
                  Share your first document
                </h3>
                <p className="mb-5 max-w-sm text-sm text-slate-500">
                  Upload a file and send a professional, trackable share link in
                  minutes.
                </p>
                <Button asChild className="rounded-xl">
                  <Link href="/smart-share">
                    <Share2 className="mr-2 h-4 w-4" /> Smart Share
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {recentFiles.map((file) => (
                  <Link
                    key={file.contentItemId}
                    href="/shared-files"
                    className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-3 transition-all hover:border-blue-200 hover:shadow-sm"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900 group-hover:text-blue-600">
                        {file.name}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Shared {file.shareCount}× · {file.firstViewCount} opened
                      </p>
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Intl.DateTimeFormat(undefined, {
                        dateStyle: 'medium',
                      }).format(new Date(file.latestSharedAt))}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200/80 shadow-sm">
          <CardContent className="p-4">
            <p className="mb-3 text-sm font-semibold text-slate-900">
              Quick actions
            </p>
            <div className="space-y-2">
              <Button
                asChild
                className="w-full justify-start rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
              >
                <Link href="/smart-share">
                  <Send className="mr-2 h-4 w-4" /> New Smart Share
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full justify-start rounded-xl"
              >
                <Link href="/shared-files">
                  <FolderOpen className="mr-2 h-4 w-4" /> Browse Shared Files
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
